import type { Preferences } from '@cobliteam/common-types';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { Button, Typography } from '@cobliteam/ui/components';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Input from '@cobliteam/ui/form/Input';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useEffect, useState } from 'react';

const preferenceKeys: Array<keyof Preferences> = [
  'displayLicensePlate',
  'displayVehiclesCluster',
  'displayServiceCard',
  'displayMonitoringGeofences',
  'displayMonitoringGeofencesForceVisibility',
  'displayTrackingGeofences',
  'isOpenSidebar',
  'idleEngineReportThreshold',
  'reportsMailList',
  'geofenceSuccessModal',
  'geofenceTourSeen',
  'hideGeofenceFormatBanner',
  'useMapMatch',
  'isFirstTriggersAccess',
  'hideScheduledMaintenanceBanner',
  'hideVehicleGroupsPreferencesBanner',
  'hideTriggerEventsInfoBanner',
  'hideNewTriggersFeatureInfoBanner',
  'hideIdleEngineOnCalibrationBanner',
  'showSignalTour',
  'hasAgreedNominationNotification',
  'showUserFeedbackTypeform',
  'hasCompletedTripImageHistoryTutorial',
  'showIncidentBanner',
  'openSiteVisibility',
  'disableAnsweredUpsellResearchDialog',
  'answeredUpsellResearchDialog',
  'shownTwoTimesThisWeek',
  'upsellResearchDialogLastShownDate',
  'hideCoblicamPLGIdleEngine',
  'hideCoblicamPLGFleetSafety',
  'safetyRankingTypePage',
  'vehicleGroupIds',
  'enableVehicleDriverAllowList',
  'mediaEventLiveUpdate',
  'typeformSurvey',
  'hideDriverAssociationPastConfirmation',
  'appJoyride',
  'insights',
];

const PreferencesManager = () => {
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const [preferencesState, setPreferencesState] = useState<Record<string, any>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showMissingOnly, setShowMissingOnly] = useState(false);

  useEffect(() => {
    if (preferences) {
      setPreferencesState({ ...preferences });
    }
  }, [preferences]);

  const handleTogglePreference = (key: string, value: any) => {
    if (!preferences) return;

    const newPreferences = { ...preferences } as Record<string, any>;

    if (typeof value === 'boolean') {
      newPreferences[key] = !value;
    } else if (value === undefined) {
      newPreferences[key] = true;
    } else {
      // If it's not a boolean, we'll just remove it
      delete newPreferences[key];
    }

    setPreferences(newPreferences as Preferences);
  };

  const handleCycleState = (key: string, value: any) => {
    if (!preferences) return;

    const newPreferences = { ...preferences } as Record<string, any>;

    if (value === undefined) {
      // Not set -> true
      newPreferences[key] = true;
    } else if (value === true) {
      // true -> false
      newPreferences[key] = false;
    } else if (value === false) {
      // false -> not set (removed)
      delete newPreferences[key];
    } else {
      // If it's not a boolean, we'll just remove it
      delete newPreferences[key];
    }

    setPreferences(newPreferences as Preferences);
  };

  const handleReset = () => {
    if (confirm('Tem certeza que deseja resetar todas as preferências? Esta ação não pode ser desfeita.')) {
      localStorage.removeItem('preferences');
      window.location.reload();
    }
  };

  const renderPreferenceValue = (value: any): string => {
    if (value === undefined) return 'Não definida';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object' && value !== null) return 'Object';
    return String(value);
  };

  // Get all keys including those from the interface and those in localStorage
  const getAllPreferenceKeys = () => {
    const allKeys = new Set([...preferenceKeys, ...Object.keys(preferencesState)]);

    return Array.from(allKeys)
      .filter((key) => {
        // Apply search filter
        if (searchTerm && !key.toLowerCase().includes(searchTerm.toLowerCase())) {
          return false;
        }

        // Apply "missing only" filter
        if (showMissingOnly && preferencesState[key] !== undefined) {
          return false;
        }

        return true;
      })
      .sort();
  };

  if (!preferences) {
    return (
      <div className='flex flex-col gap-4'>
        <Typography variant='heading-1'>Preferências</Typography>

        <div className='flex flex-col items-center gap-2'>
          <Typography variant='body'>Nenhuma preferência encontrada no localStorage</Typography>
          <Button variant='outlined' color='primary' size='sm' onClick={() => setPreferences({} as Preferences)}>
            Inicializar preferências
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between'>
        <Typography variant='heading-1'>Preferências</Typography>
        <Button variant='outlined' color='danger' size='sm' onClick={handleReset}>
          Resetar todas
        </Button>
      </div>

      <div className='flex items-center gap-2'>
        <div className='w-full'>
          <Input type='text' size='sm' placeholder='Pesquisar...' value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className='flex items-center gap-2'>
          <Checkbox id='showMissingOnly' checked={showMissingOnly} onCheckedChange={() => setShowMissingOnly(!showMissingOnly)} />
          <Label htmlFor='showMissingOnly' className='whitespace-nowrap'>
            Apenas não definidas ({getAllPreferenceKeys().filter((key) => preferencesState[key] === undefined).length})
          </Label>
        </div>
      </div>

      <div className='overflow-hidden rounded bg-white shadow'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-gray-9'>
            <tr>
              <th className='p-2'>
                <Typography variant='body' color='white'>
                  <strong>Chave</strong>
                </Typography>
              </th>
              <th>
                <Typography variant='body' color='white'>
                  <strong>Valor</strong>
                </Typography>
              </th>
              <th>
                <Typography variant='body' color='white'>
                  <strong>Ativar/Desativar</strong>
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-5 bg-white'>
            {getAllPreferenceKeys().map((key) => {
              const value = preferencesState[key];
              const isUndefined = value === undefined;
              const isBoolean = typeof value === 'boolean';

              return (
                <tr key={key} className={isUndefined ? 'bg-gray-2' : ''}>
                  <td className='p-2'>
                    <Typography variant='caption-xs' className='flex items-center gap-1 whitespace-nowrap'>
                      {key}
                    </Typography>
                  </td>
                  <td>
                    <div className='flex items-center gap-2'>
                      <Typography variant='caption-xs' className='whitespace-nowrap'>
                        {renderPreferenceValue(value)}
                      </Typography>
                    </div>
                  </td>
                  <td>
                    {isBoolean || isUndefined ? (
                      <div className='flex items-center gap-2'>
                        <Checkbox id={key} checked={value === true} onCheckedChange={() => handleCycleState(key, value)} size='sm' />
                        <Label htmlFor={key}>
                          <Icon name={value === true ? 'check' : value === false ? 'x' : 'circle-slash-2'} className='!size-3' />
                        </Label>
                      </div>
                    ) : (
                      <Button variant='link' color='danger' className='!text-xs h-fit p-0 text-start' onClick={() => handleCycleState(key, value)}>
                        Remover
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PreferencesManager;
